import { isDevelopment } from '@/helpers';
import { type KycAnswers } from '@/models';
import {
  type Application,
  type ApplicationDetailsResponseDTO,
  type ConsentDataDTO,
  ConsentDataDTOProposalType,
  ConsentDataDTOStatus,
  ConsentDataDTOType,
  ConsentDataRequestDTOStatus,
  type ConsentViewDTO,
  type ConsentViewRequestDTO,
  type SubmitApplicationRequest,
} from '@/types/consumer-deposit-application-service';
import {
  type KycSurvey,
  type Question,
  QuestionType,
} from '@/types/know-your-customer-service';

import { SectionType } from './sectionUtils';

type KycGroup = {
  id: string;
  questions: { [key: string]: Question };
  questionsIds: number[];
  title: string;
  type: string;
};
type Questions = {
  [key: string]: Question;
};

export const headers = (jwt: string) => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${jwt}`,
    'Content-Type': 'application/json',
  };

  if (!isDevelopment) headers.apikey = import.meta.env.VITE_RESURS_API_KEY;

  return headers;
};

export const parseSaveRequest = (
  data: ApplicationDetailsResponseDTO,
): Application => {
  const consents = parseConsents(data.consents);

  return {
    ...data,
    consents,
  };
};

export const parseSubmitRequest = (
  onCancelledUri: string,
  onSuccessUri: string,
  kycSurvey: KycSurvey,
  data: ApplicationDetailsResponseDTO,
  sectionMap: SectionType[] = [],
): SubmitApplicationRequest => {
  const hasValidBankruptcyDetails = sectionMap.includes(SectionType.Bankruptcy)
    ? Boolean(data.bankruptcyDetails)
    : true;

  if (
    data.email &&
    data.kycAnswers &&
    data.phoneNumber &&
    data.productCode &&
    hasValidBankruptcyDetails
  ) {
    return {
      autogiroSettings: data.autogiroSettings,
      bankruptcyDetails: data.bankruptcyDetails,
      campaignCode: data?.campaignCode,
      consents: parseConsents(data.consents),
      depositGuaranteeAccepted: true,
      disbursementAccount: data.disbursementAccount,
      email: data.email,
      kycAnswers: formatAnswers(
        kycSurvey,
        data.kycAnswers,
      ) as unknown as SubmitApplicationRequest['kycAnswers'],
      onCancelledUri,
      onSuccessUri,
      phoneNumber: data.phoneNumber,
      productCode: data.productCode,
      termsConditionsAccepted: true,
    };
  }
  throw Error('Could not parse submit request');
};

const formatAnswers = (survey: KycSurvey, answers: KycAnswers) => {
  const result: KycAnswers = {};

  return Object.entries(answers).reduce((kycAnswers, [groupName, group]) => {
    const groupResult: Record<string, Array<string>> = {};

    kycAnswers[groupName] = Object.entries(group).reduce(
      (questions, [questionId, answersForQuestion]) => {
        const initial: { [key: string]: KycGroup } = {};
        const groups =
          survey?.groups.reduce((map, group) => {
            let questions: Questions = {};

            questions = group.questions.reduce((map, question) => {
              map[question.id] = question;

              return map;
            }, questions);
            map[group.id] = {
              id: group.id,
              questions,
              questionsIds: group.questions
                .filter((question) => !question.displayCondition)
                .map((question) => question.id)
                .sort((questionIdA, questionIdB) => questionIdA - questionIdB)
                .map((questionId) => questionId),
              title: group.title,
              type: group.type,
            };

            return map;
          }, initial) ?? {};

        if (!groups) {
          throw new Error(`Group ${groupName} does not exist`);
        }
        const question = groups[groupName].questions[questionId];

        if (!question) {
          throw new TypeError(
            `Question ${questionId} does not exist in group ${groupName}`,
          );
        }
        if (
          [
            QuestionType.COUNTRY_CODE_SELECTION,
            QuestionType.MULTIPLE_COUNTRY_CODE_SELECTION,
            QuestionType.FREE_TEXT,
          ].includes(question.type)
        )
          questions[questionId] = answersForQuestion;
        else {
          questions[questionId] = answersForQuestion
            .map(
              (optionId) =>
                (question.options ?? []).find(
                  (option) => option.id === Number(optionId),
                )?.text,
            )
            .filter((value) => value !== undefined);
        }

        return questions;
      },
      groupResult,
    );

    return kycAnswers;
  }, result);
};

const parseConsents = (
  consents: ConsentViewDTO[] | undefined,
): ConsentViewRequestDTO[] | undefined => {
  if (!consents) return undefined;

  const result = consents
    .filter((consent) => consent.data !== undefined)
    .map((consent) => ({
      id: consent.id,
      proposals: (consent.data as ConsentDataDTO[])
        .filter((data) => data.type === ConsentDataDTOType.CONSENT)
        .map((item) => ({
          id: item.id,
          status: parseStatus(item.proposalType, item.status),
        })),
    }));

  return result;
};

const parseStatus = (
  proposalType: ConsentDataDTOProposalType | undefined,
  status: ConsentDataDTOStatus | undefined,
): ConsentDataRequestDTOStatus => {
  if (status === ConsentDataDTOStatus.OBTAINED)
    return ConsentDataRequestDTOStatus.OBTAINED;

  if (proposalType === ConsentDataDTOProposalType.SOFT_OPT_IN)
    return ConsentDataRequestDTOStatus.NONE;
  if (status === ConsentDataDTOStatus.REVOKED)
    return ConsentDataRequestDTOStatus.REVOKED;

  if (proposalType === ConsentDataDTOProposalType.OPT_IN)
    return ConsentDataRequestDTOStatus.REVOKED;

  if (proposalType === ConsentDataDTOProposalType.OPT_OUT)
    return ConsentDataRequestDTOStatus.OBTAINED;

  return ConsentDataRequestDTOStatus.NONE;
};
